import React from 'react';
import './ui/Shared.css';
import { logger } from '../utils/logger.js';

const LOG_MODULE = 'ErrorBoundary';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
    this.handleRetry = this.handleRetry.bind(this);
    this.handleReload = this.handleReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    logger.error(LOG_MODULE, 'Unhandled React error', {
      level: this.props.level || 'feature',
      error: error instanceof Error
        ? {
          name: error.name,
          message: error.message,
          stack: error.stack
        }
        : String(error),
      componentStack: errorInfo?.componentStack || ''
    });
  }

  handleRetry() {
    this.setState({
      hasError: false,
      error: null
    });
  }

  handleReload() {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  renderAppFallback() {
    return (
      <div className="content centered-layout" role="alert">
        <section className="card section">
          <h2>Something went wrong</h2>
          <p>An unexpected error occurred. Reload the page to recover.</p>
          <div className="inline-actions">
            <button type="button" className="btn primary" onClick={this.handleReload}>
              Reload
            </button>
          </div>
        </section>
      </div>
    );
  }

  renderFeatureFallback() {
    return (
      <section className="card section" role="alert">
        <h3>Something went wrong</h3>
        <p>This section failed to render. Please try again.</p>
        <div className="inline-actions">
          <button type="button" className="btn ghost" onClick={this.handleRetry}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  render() {
    const { children, fallback, level } = this.props;

    if (!this.state.hasError) {
      return children;
    }

    if (fallback) {
      if (typeof fallback === 'function') {
        return fallback({
          error: this.state.error,
          reset: this.handleRetry,
          reload: this.handleReload
        });
      }
      return fallback;
    }

    if (level === 'app') {
      return this.renderAppFallback();
    }

    return this.renderFeatureFallback();
  }
}

ErrorBoundary.defaultProps = {
  fallback: null,
  level: 'feature'
};

export default ErrorBoundary;
